import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Shield,
  ShieldCheck,
  RefreshCw,
  UserPlus,
  MapPin,
  FileText,
  Edit,
  Key,
  Trash2,
  Search,
  CheckCircle2,
  AlertTriangle,
  Users,
  LayoutGrid,
  Eye,
  BarChart3,
} from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToUserInsightCards } from "@/utils/mistralInsightMappers";
import {
  ROLE_META,
  buildPermissionMatrix,
  QUICK_ACTIONS,
  buildAccessHealth,
  buildHeroKpis,
  buildRoleCards,
  buildGovernanceChecks,
  buildAccessMaturity,
  buildInsightCards,
  buildUserManagementMarkdown,
  filterUsers,
  getUserScope,
  getUserInitials,
  getUserSubtitle,
} from "@/utils/userManagementDerived";
import "./UserManagement.css";
import { toTitleCase } from "../utils/toTitleCase";

const BACKEND_URL = getBackendUrl();

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "state_officer", label: "State Officer" },
  { value: "district_officer", label: "District Officer" },
  { value: "viewer", label: "Viewer" },
];

const ROLE_ICONS = {
  admin: Shield,
  state_officer: MapPin,
  district_officer: LayoutGrid,
  viewer: Eye,
};

const QUICK_ICONS = {
  add: UserPlus,
  permissions: ShieldCheck,
  scope: MapPin,
  audit: FileText,
};

const GOVERNANCE_ICONS = {
  green: CheckCircle2,
  orange: AlertTriangle,
  "": FileText,
  purple: Users,
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
};

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    password: "",
    role: "viewer",
    district_code: "",
    is_active: true,
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/users`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const buildLocalMarkdown = useCallback(() => buildUserManagementMarkdown(users), [users]);
  const scopeKey = useMemo(
    () => JSON.stringify({ n: users.length, a: users.filter((u) => u.is_active).length }),
    [users]
  );
  const { loading: aiLoading, pickMapped, isMistral } = useMistralNarrativeOverlay({
    kind: "user-management",
    title: "User Management Insights",
    buildLocalMarkdown,
    context: { users },
    enabled: !loading,
    scopeKey,
  });

  const accessHealth = useMemo(() => buildAccessHealth(users), [users]);
  const heroKpis = useMemo(() => buildHeroKpis(users), [users]);
  const roleCards = useMemo(() => buildRoleCards(users), [users]);
  const permissionMatrix = useMemo(() => buildPermissionMatrix(users), [users]);
  const governanceChecks = useMemo(() => buildGovernanceChecks(users), [users]);
  const accessMaturity = useMemo(() => buildAccessMaturity(users), [users]);
  const localInsightCards = useMemo(() => buildInsightCards(users), [users]);
  const insightCards = useMemo(
    () => pickMapped(localInsightCards, "insights", bulletsToUserInsightCards),
    [pickMapped, localInsightCards]
  );
  const filteredUsers = useMemo(
    () => filterUsers(users, { search: searchQuery, role: roleFilter, status: statusFilter }),
    [users, searchQuery, roleFilter, statusFilter]
  );

  const activeCount = users.filter((u) => u.is_active).length;
  const usersHealthy = users.every((u) => u.is_active);

  const handleCreate = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/users`, formData);
      toast.success("User created successfully");
      setShowCreateDialog(false);
      setFormData({
        email: "",
        full_name: "",
        password: "",
        role: "viewer",
        district_code: "",
        is_active: true,
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {
        full_name: formData.full_name,
        role: formData.role,
        district_code: formData.district_code || null,
        is_active: formData.is_active,
      };
      await axios.put(`${BACKEND_URL}/api/auth/users/${selectedUser.id}`, updateData);
      toast.success("User updated successfully");
      setShowEditDialog(false);
      fetchUsers();
    } catch {
      toast.error("Failed to update user");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/users/${userId}/reset-password`);
      toast.success(`Password reset. Temporary password: ${response.data.temporary_password}`);
    } catch {
      toast.error("Failed to reset password");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      district_code: user.district_code || "",
      is_active: user.is_active,
    });
    setShowEditDialog(true);
  };

  const handleQuickAction = (action) => {
    if (action.id === "add") {
      setShowCreateDialog(true);
      return;
    }
    scrollToSection(action.target);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchUsers();
  };

  const resetCreateForm = () => {
    setFormData({
      email: "",
      full_name: "",
      password: "",
      role: "viewer",
      district_code: "",
      is_active: true,
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="user-management">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="user-management-page" data-testid="user-management">
      <section className="um-page-header" id="um-overview">
        <div>
          <div className="um-eyebrow">
            <ShieldCheck size={18} />
            Admin control · Access & roles · 2025–26
          </div>
          <h1>User Management</h1>
          <p>
            Manage user accounts, access scope and role permissions from a focused administration
            console with clear security status and faster role governance.
          </p>
        </div>
        <div className="um-header-actions">
          <button type="button" className="um-ghost-action" onClick={handleRefresh}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button type="button" className="um-primary-action" onClick={() => setShowCreateDialog(true)}>
            <UserPlus size={18} />
            Add User
          </button>
        </div>
      </section>

      <section className="um-hero-card" aria-label="Access health summary">
        <div className="um-hero-layout">
          <div>
            <div className="um-access-main">
              <div
                className="um-shield-score"
                style={accessHealth.ringStyle}
                aria-label={`Access Health ${accessHealth.score} percent`}
              >
                <div>
                  <strong>{accessHealth.score}%</strong>
                  <span>Access Health</span>
                </div>
              </div>
              <div className="um-hero-copy">
                <h2>{toTitleCase(accessHealth.headline)}</h2>
                <p>{accessHealth.narrative}</p>
                <div className="um-status-row">
                  {accessHealth.chips.map((chip) => (
                    <span key={chip.label} className={`um-status-chip ${chip.className || ""}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="um-summary-note">
              <div className="um-icon-box purple">
                <Users size={22} />
              </div>
              <div>
                <b>Recommended executive action</b>
                <span>{accessHealth.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="um-metric-grid" aria-label="User management KPIs">
            {heroKpis.map((kpi) => (
              <article key={kpi.label} className="um-metric-card">
                <small>{kpi.label}</small>
                <strong>{kpi.value}</strong>
                <span className={kpi.subClass || ""}>{kpi.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="um-quick-grid" aria-label="Quick administration actions">
        {QUICK_ACTIONS.map((action) => {
          const Icon = QUICK_ICONS[action.id] || UserPlus;
          return (
            <button
              key={action.id}
              type="button"
              className="um-quick-card"
              onClick={() => handleQuickAction(action)}
            >
              <div className="um-quick-top">
                <div>
                  <div className={`um-icon-box ${action.iconClass || ""}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <h3>{toTitleCase(action.title)}</h3>
                  <p>{action.description}</p>
                </div>
              </div>
              <span className="um-quick-arrow">→</span>
            </button>
          );
        })}
      </section>

      <div className="um-section-title" id="um-users">
        <div>
          <h2>User Directory</h2>
          <p>
            Search, filter and govern users in one place. The row layout gives the user identity,
            role, scope and actions without visual clutter.
          </p>
        </div>
        <button type="button" className="um-section-action" onClick={() => scrollToSection("um-permissions")}>
          View permissions →
        </button>
      </div>

      <section className="um-main-grid">
        <article className="um-panel" aria-label="Users table">
          <div className="um-panel-head">
            <div>
              <h3>Users</h3>
              <p>
                {activeCount} active account{activeCount === 1 ? "" : "s"} in the selected
                administrative scope.
              </p>
            </div>
            <span className={`um-pill ${usersHealthy ? "green" : "orange"}`}>
              {usersHealthy ? "Healthy" : "Review"}
            </span>
          </div>

          <div className="um-toolbar">
            <label className="um-local-search">
              <Search size={18} />
              <input
                placeholder="Search users by name, email or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
            <label className="um-select-pill">
              Role:
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">All</option>
                {ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="um-select-pill">
              Status:
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>

          <div className="um-user-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", color: "var(--um-muted)" }}>
                      No users match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const meta = ROLE_META[user.role] || ROLE_META.viewer;
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="um-user-person">
                            <span className="um-person-avatar">{getUserInitials(user.full_name)}</span>
                            <div>
                              <b>{user.full_name}</b>
                              <span>{getUserSubtitle(user)}</span>
                            </div>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`um-role-name ${meta.roleClass}`}>{meta.label}</span>
                        </td>
                        <td>{getUserScope(user)}</td>
                        <td>
                          <span className={`um-status-badge ${user.is_active ? "" : "inactive"}`}>
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div className="um-action-icons">
                            <button
                              type="button"
                              className="um-mini-icon"
                              title="Edit"
                              onClick={() => openEditDialog(user)}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              className="um-mini-icon"
                              title="Reset access"
                              onClick={() => handleResetPassword(user.id)}
                            >
                              <Key size={16} />
                            </button>
                            <button
                              type="button"
                              className="um-mini-icon danger"
                              title="Delete"
                              onClick={() => handleDelete(user.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </article>

        <aside className="um-panel" aria-label="Access governance">
          <div className="um-panel-head">
            <div>
              <h3>Access Governance</h3>
              <p>Controls that keep the admin surface safe and accountable.</p>
            </div>
            <span className="um-pill">{governanceChecks.length} checks</span>
          </div>

          <div className="um-governance-list">
            {governanceChecks.map((check) => {
              const Icon = GOVERNANCE_ICONS[check.iconClass] || FileText;
              return (
                <div key={check.title} className="um-governance-item">
                  <div className={`um-icon-box ${check.iconClass || ""}`.trim()}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h4>{check.title}</h4>
                    <p>{check.description}</p>
                  </div>
                  <span className={`um-pill ${check.pillClass}`}>{check.status}</span>
                </div>
              );
            })}
          </div>

          <div className="um-progress-card">
            <b>Access maturity</b>
            <span>{accessMaturity.description}</span>
            <div className="um-bar-line">
              <i style={{ width: `${accessMaturity.pct}%` }} />
            </div>
          </div>
        </aside>
      </section>

      <div className="um-section-title" id="um-permissions">
        <div>
          <h2>Role Permissions</h2>
          <p>
            Permissions are redesigned as both role cards and a matrix so administrators can
            understand access rights quickly.
          </p>
        </div>
        <button type="button" className="um-section-action" onClick={() => scrollToSection("um-audit")}>
          View control notes →
        </button>
      </div>

      <section className="um-role-grid" aria-label="Role summary cards">
        {roleCards.map((role) => {
          const Icon = ROLE_ICONS[role.id] || Shield;
          return (
            <article key={role.id} className="um-role-card">
              <div className="um-role-head">
                <div className={`um-icon-box ${role.iconClass || ""}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`um-role-name ${role.meta.roleClass}`}>{role.meta.label}</span>
              </div>
              <h3>{toTitleCase(role.title)}</h3>
              <p>{role.description}</p>
              <div className="um-role-count">
                <strong>{role.count}</strong>
                <span>assigned user{role.count === 1 ? "" : "s"}</span>
              </div>
              <div className="um-permission-dots">
                {role.permissions.map((perm) => (
                  <span key={perm.label} className={`um-perm-dot ${perm.muted ? "muted" : ""}`.trim()}>
                    {perm.label}
                  </span>
                ))}
              </div>
            </article>
          );
        })}
      </section>

      <section className="um-panel um-matrix-panel" aria-label="Permission matrix">
        <div className="um-matrix-header">
          <div>
            <h3>Permission Matrix</h3>
            <p>
              Compact matrix retained from the original page, with clearer role badges and stronger
              visual distinction between allowed, limited and blocked rights.
            </p>
          </div>
          <span className="um-pill">4 roles · 4 permissions</span>
        </div>
        <div className="um-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>View All</th>
                <th>Export</th>
                <th>Manage Users</th>
                <th>Import Data</th>
                <th>Risk Level</th>
                <th>Recommended Control</th>
              </tr>
            </thead>
            <tbody>
              {permissionMatrix.map((row) => {
                const meta = ROLE_META[row.role];
                return (
                  <tr key={row.role}>
                    <td>
                      <span className={`um-role-name ${meta.roleClass}`}>{meta.label}</span>
                    </td>
                    <td className={`um-${row.view.className}`}>{row.view.text}</td>
                    <td className={`um-${row.export.className}`}>{row.export.text}</td>
                    <td className={`um-${row.users.className}`}>{row.users.text}</td>
                    <td className={`um-${row.import.className}`}>{row.import.text}</td>
                    <td>
                      <span className={`um-pill ${row.risk.className || ""}`.trim()}>{row.risk.label}</span>
                    </td>
                    <td>{row.control}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="um-section-title" id="um-audit">
        <div>
          <h2>Controls and Next Actions</h2>
          <p>
            Move the page from a static admin table to an actionable access-governance workspace.{" "}
            <span className="um-ai-pill">
              {aiLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
            </span>
          </p>
        </div>
        <button type="button" className="um-section-action" onClick={() => scrollToSection("um-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="um-insight-grid" aria-label="Access insights">
        {insightCards.map((card) => {
          const Icon = card.iconClass === "green" ? CheckCircle2 : card.iconClass === "purple" ? FileText : BarChart3;
          return (
            <article key={card.title} className="um-insight-card">
              <div className={`um-icon-box ${card.iconClass || ""}`.trim()}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.description}</p>
              <div className="um-insight-list">
                {card.rows.map((row) => (
                  <span key={row.text} className={`um-insight-row ${row.className || ""}`.trim()}>
                    {row.text}
                  </span>
                ))}
              </div>
            </article>
          );
        })}
      </section>

      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 characters"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role === "district_officer" && (
              <div className="space-y-2">
                <Label>District Code</Label>
                <Input
                  value={formData.district_code}
                  onChange={(e) => setFormData({ ...formData, district_code: e.target.value })}
                  placeholder="e.g., PUNE"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email} disabled className="bg-slate-100" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role === "district_officer" && (
              <div className="space-y-2">
                <Label>District Code</Label>
                <Input
                  value={formData.district_code}
                  onChange={(e) => setFormData({ ...formData, district_code: e.target.value })}
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
